import React, { useState, useEffect } from 'react';
import { X, Clock, User, FileSignature, Shield, Upload, Globe } from 'lucide-react';
import { documentService } from '../services/documentService';

const AuditTrailModal = ({ documentId, documentName, onClose }) => {
  const [auditEvents, setAuditEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAuditTrail = async () => {
      try {
        setIsLoading(true);
        const events = await documentService.getDocumentAuditTrail(documentId);
        setAuditEvents(events);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuditTrail();
  }, [documentId]);

  const getEventIcon = (type) => {
    switch (type) {
      case 'document_created': return <Upload size={16} className="text-blue-500" />;
      case 'document_signed': return <FileSignature size={16} className="text-green-500" />;
      case 'permission_granted': return <Shield size={16} className="text-purple-500" />;
      default: return <Clock size={16} className="text-gray-500" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Audit Trail</h2>
            <p className="text-sm text-gray-500 mt-1">{documentName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-2">Error loading audit trail</div>
              <div className="text-sm text-gray-500">{error}</div>
            </div>
          ) : auditEvents.length === 0 ? (
            <div className="text-center py-12">
              <Clock size={48} className="text-gray-300 mx-auto mb-4" />
              <div className="text-gray-500">No audit events found</div>
            </div>
          ) : (
            <div className="space-y-4">
              {auditEvents.map((event) => (
                <div key={event.id} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 mt-1">
                    {getEventIcon(event.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-gray-900">{event.user?.name || 'Unknown User'}</span>
                      <span className="text-sm text-gray-500">{event.details}</span>
                    </div>
                    <div className="text-sm text-gray-500 space-y-1">
                      <div>{formatTimestamp(event.timestamp)}</div>
                      {event.ipAddress && (
                        <div className="flex items-center space-x-1">
                          <Globe size={12} />
                          <span>IP: {event.ipAddress}</span>
                        </div>
                      )}
                      {event.user?.email && (
                        <div className="flex items-center space-x-1">
                          <User size={12} />
                          <span>{event.user.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditTrailModal; 